import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, clientNavItems } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  FileSignature,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Building2,
  Calendar,
  MapPin,
  DollarSign,
  User,
  Shield,
  Pen
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractSign() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState("");

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const res = await axios.get(`${API}/contracts`);
      const found = res.data.find(c => c.contract_id === contractId);
      if (!found) {
        toast.error("Contract negăsit");
        navigate(-1);
        return;
      }
      setContract(found);
      
      // Fetch project details
      if (found.project_id) {
        const projectRes = await axios.get(`${API}/projects/${found.project_id}`);
        setProject(projectRes.data);
      }
    } catch (err) {
      console.error("Error fetching contract:", err);
      toast.error("Failed to load contract");
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!agreedToTerms) {
      toast.error("Trebuie să accepți termenii și condițiile");
      return;
    }
    if (!signerName.trim()) {
      toast.error("Te rugăm să introduci numele complet");
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/contracts/${contractId}/sign`, {
        signer_name: signerName
      });
      toast.success("Contract semnat cu succes!");
      fetchContract(); // Refresh to show signed status
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la semnare");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={clientNavItems} title="Semnare Contract">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={clientNavItems} title="Semnare Contract">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="contract-sign-page">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Înapoi
        </Button>

        {/* Contract Header */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                <FileSignature className="w-6 h-6 text-[#23F7DD]" />
                Contract de Servicii
              </CardTitle>
              <Badge className={contract?.status === "signed" 
                ? "bg-green-500/20 text-green-400 border-green-500/30"
                : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
              }>
                {contract?.status === "signed" ? (
                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Signed</>
                ) : (
                  <><Clock className="w-4 h-4 mr-1" /> Awaiting signature</>
                )}
              </Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Project Summary */}
        {project && (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg">Project Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-[#23F7DD] mt-0.5" />
                <div>
                  <p className="text-xs text-[#94A3B8]">Project</p>
                  <p className="text-white font-medium">{project.title}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#23F7DD] mt-0.5" />
                <div>
                  <p className="text-xs text-[#94A3B8]">Locație</p>
                  <p className="text-white">{project.location_city}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#23F7DD] mt-0.5" />
                <div>
                  <p className="text-xs text-[#94A3B8]">Data început</p>
                  <p className="text-white">
                    {project.start_date ? new Date(project.start_date).toLocaleDateString("ro-RO") : "-"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <p className="text-xs text-[#94A3B8]">Valoare</p>
                  <p className="text-green-400 font-bold">{project.quoted_price || project.budget || "N/A"} NOK</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contract Text */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg">Termeni și Condiții</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-black/50 rounded-lg p-6 border border-white/5">
              <pre className="whitespace-pre-wrap text-[#94A3B8] text-sm font-mono leading-relaxed">
                {contract?.contract_text}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Signing Section */}
        {contract?.status === "pending" ? (
          <Card className="bg-[#0A0A0A] border-[#23F7DD]/30">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Pen className="w-5 h-5 text-[#23F7DD]" />
                Semnare Contract
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Agreement Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-1 border-[#23F7DD] data-[state=checked]:bg-[#23F7DD] data-[state=checked]:text-[#050505]"
                />
                <label htmlFor="terms" className="text-[#94A3B8] text-sm cursor-pointer">
                  Confirm că am citit și înțeles termenii și condițiile acestui contract de servicii. 
                  Înțeleg că acest contract reprezintă un acord obligatoriu din punct de vedere juridic 
                  între părți.
                </label>
              </div>

              {/* Signature Input */}
              <div className="space-y-2">
                <label className="text-sm text-[#94A3B8]">
                  Semnătură digitală (numele complet) *
                </label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Introduceți numele complet"
                  className="bg-black/50 border-white/10 text-white font-['Caveat',cursive] text-2xl h-14"
                  data-testid="signer-name-input"
                />
              </div>

              {/* Sign Button */}
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleSign}
                  disabled={signing || !agreedToTerms || !signerName.trim()}
                  className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold px-8 py-6 text-lg disabled:opacity-50"
                  data-testid="sign-contract-btn"
                >
                  {signing ? (
                    <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <FileSignature className="w-5 h-5 mr-2" />
                  )}
                  Semnează Contractul
                </Button>
                <div className="flex items-center gap-2 text-[#94A3B8] text-sm">
                  <Shield className="w-4 h-4" />
                  Semnătură electronică securizată
                </div>
              </div>
            </CardContent>
          </Card>
        ) : contract?.status === "signed" ? (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-500/20 rounded-full">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-green-400 mb-2">Contract Semnat</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-[#94A3B8]">Signed by:</p>
                      <p className="text-white flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {contract.signer_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8]">Data semnării:</p>
                      <p className="text-white">
                        {contract.signed_at 
                          ? new Date(contract.signed_at).toLocaleString("ro-RO")
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
